import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { getNextVoucherNo } from "../services/voucherNumberService";

const emptyLine = { accountId: "", debit: "", credit: "" };

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  tableWrap: {
    maxHeight: 400,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 800, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "5px 6px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: { fontSize: 10, padding: "4px 7px", borderRadius: 20 },
};

export default function JournalPage() {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    voucherNo: "",
    entryDate: new Date().toISOString().split("T")[0],
    description: "",
    details: [{ ...emptyLine }, { ...emptyLine }],
  });

  useEffect(() => {
    loadAccounts();
    loadNextJournalNo();
  }, []);

  const loadNextJournalNo = async () => {
    const no = await getNextVoucherNo("journal", form.entryDate);
    setForm((p) => (p.voucherNo ? p : { ...p, voucherNo: no }));
  };

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const res = await client.get("/accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Unable to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const addRow = () => {
    setForm((prev) => ({
      ...prev,
      details: [...prev.details, { ...emptyLine }],
    }));
  };

  const removeRow = (index) => {
    if (form.details.length <= 2) return alert("Minimum 2 rows required");

    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
  };

  const updateLine = (index, field, value) => {
    const updated = [...form.details];
    updated[index][field] = value;

    if (field === "debit" && Number(value) > 0) updated[index].credit = "";
    if (field === "credit" && Number(value) > 0) updated[index].debit = "";

    setForm({ ...form, details: updated });
  };

  const totalDebit = useMemo(
    () => form.details.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0),
    [form.details],
  );

  const totalCredit = useMemo(
    () => form.details.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0),
    [form.details],
  );

  const difference = Math.abs(totalDebit - totalCredit);
  const isBalanced = totalDebit === totalCredit && totalDebit > 0;

  const saveJournal = async () => {
    if (!isBalanced) return alert("Debit and Credit must match");

    const payload = {
      companyId: Number(localStorage.getItem("companyId") || 0),
      voucherNo: form.voucherNo,
      entryDate: form.entryDate,
      description: form.description,
      details: form.details.map((r) => ({
        accountId: Number(r.accountId),
        debit: parseFloat(r.debit) || 0,
        credit: parseFloat(r.credit) || 0,
      })),
    };

    try {
      setSaving(true);
      await client.post("/finance/journal", payload);
      alert("Saved Successfully");

      setForm({
        voucherNo: "",
        entryDate: new Date().toISOString().split("T")[0],
        description: "",
        details: [{ ...emptyLine }, { ...emptyLine }],
      });
    } catch {
      alert("Error saving journal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-journal-text me-2"></i>
            Journal Entry
          </h6>
          <div style={styles.sub}>Manual double-entry voucher posting</div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                placeholder="Voucher No"
                value={form.voucherNo}
                onChange={(e) =>
                  setForm({ ...form, voucherNo: e.target.value })
                }
              />
            </div>

            <div className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.entryDate}
                onChange={(e) =>
                  setForm({ ...form, entryDate: e.target.value })
                }
              />
            </div>

            <div className="col-md-6">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                placeholder="Example: Salary expense for May 2026 / Cash deposited into bank / GST adjustment entry"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                style={styles.btn}
                onClick={addRow}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Row
              </button>

              <button
                className={`btn btn-sm ${isBalanced ? "btn-success" : "btn-secondary"}`}
                style={styles.btn}
                disabled={!isBalanced || saving}
                onClick={saveJournal}
              >
                <i className="bi bi-save me-1"></i>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>

            <div
              style={{
                fontSize: 10,
                padding: "5px 9px",
                borderRadius: 20,
                background: isBalanced ? "#d1e7dd" : "#f8d7da",
                color: isBalanced ? "#0f5132" : "#842029",
                fontWeight: 700,
              }}
            >
              {isBalanced ? "Balanced" : `Difference: ${difference.toFixed(2)}`}
            </div>
          </div>

          {loadingAccounts && (
            <div
              className="alert alert-info"
              style={{ fontSize: 11, padding: "7px 10px" }}
            >
              Loading accounts...
            </div>
          )}

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 45 }}>#</th>
                  <th style={{ ...styles.th, minWidth: 360 }}>Account</th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Debit
                  </th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Credit
                  </th>
                  <th style={{ ...styles.th, width: 80 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {form.details.map((row, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{i + 1}</td>

                    <td style={styles.td}>
                      <select
                        className="form-select form-select-sm"
                        style={styles.input}
                        value={row.accountId}
                        onChange={(e) =>
                          updateLine(i, "accountId", e.target.value)
                        }
                      >
                        <option value="">Select Account</option>
                        {accounts.map((a) => (
                          <option key={a.Id ?? a.id} value={a.Id ?? a.id}>
                            {(a.AccountCode ?? a.accountCode) +
                              " - " +
                              (a.AccountName ?? a.accountName)}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        className="form-control form-control-sm text-end"
                        style={styles.input}
                        value={row.debit}
                        onChange={(e) => updateLine(i, "debit", e.target.value)}
                      />
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        className="form-control form-control-sm text-end"
                        style={styles.input}
                        value={row.credit}
                        onChange={(e) =>
                          updateLine(i, "credit", e.target.value)
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{
                          ...styles.btn,
                          height: 25,
                          padding: "2px 8px",
                        }}
                        onClick={() => removeRow(i)}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr>
                  <td
                    colSpan="2"
                    style={{
                      ...styles.td,
                      fontWeight: 700,
                      background: "#f8f9fa",
                    }}
                  >
                    Total
                  </td>
                  <td
                    className="text-end"
                    style={{ ...styles.td, fontWeight: 700 }}
                  >
                    {totalDebit.toFixed(2)}
                  </td>
                  <td
                    className="text-end"
                    style={{ ...styles.td, fontWeight: 700 }}
                  >
                    {totalCredit.toFixed(2)}
                  </td>
                  <td style={styles.td}>
                    <span
                      className={`badge ${isBalanced ? "text-bg-success" : "text-bg-danger"}`}
                      style={styles.badge}
                    >
                      {isBalanced ? "OK" : "Diff"}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
